import React from 'react';
import { Edit, ExpandMore, ExpandLess, Save, Cancel } from '@mui/icons-material';

import { SchedulerSubstageRow } from '../SchedulerSubstageRow';
import { formatDate, formatTime, statusLabels, statusOptions, editableStatuses, isDue } from '../../shedular.utils';

import './SchedulerEntryRow.css';
import { SchedulerEntryRowProps } from './SchedulerEntryRow.types';
/**
 * Scheduler entry row component displaying a single scheduler entry
 * Shows entry details with expand/collapse for substages and inline editing
 *
 * Features:
 * - Expandable substages display
 * - Inline status editing for processing/failed entries
 * - Edit/save/cancel action buttons
 * - Status-based row styling
 * - Edit indicator icon when entry has been modified
 *
 * @param props - Component props containing entry data and callbacks
 * @returns JSX element containing the entry row and optional substages
 */
export function SchedulerEntryRow({
  entry,
  isExpanded,
  onToggleExpanded,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
  onUpdateEditingValue,
  isEditing,
  getEditingValue,
  hasItemChanges
}: SchedulerEntryRowProps) {
  const isEditMode = isEditing('entry', entry.id);
  const editingValue = getEditingValue('entry', entry.id);
  const entryDue = isDue(entry.effective_datetime);

  return (
    <React.Fragment>
      <tr className={`scheduler-table-row scheduler-row-${entry.status}`}>
        <td className="scheduler-table-cell scheduler-col-expand">
          {entry.substages?.length > 0 && (
            <button className="scheduler-expand-button" onClick={() => onToggleExpanded(entry.id)}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </button>
          )}
        </td>
        <td className="scheduler-table-cell scheduler-col-stage">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {entry.stage_name}
            {entry.edit_date !== entry.upload_datetime && entry.status === 'processing' && (
              <Edit sx={{ fontSize: '16px' }} className="scheduler-edit-icon" />
            )}
          </div>
        </td>
        <td className="scheduler-table-cell scheduler-col-date">
          {entry.effective_datetime ? formatDate(entry.effective_datetime) : '-'}
        </td>
        <td className="scheduler-table-cell scheduler-col-time">
          {entry.effective_datetime ? formatTime(entry.effective_datetime) : '-'}
        </td>
        <td className="scheduler-table-cell scheduler-col-status">
          {isEditMode ? (
            <select
              value={editingValue?.status || entry.status}
              onChange={e => onUpdateEditingValue('entry', entry.id, 'status', e.target.value)}
              disabled={entry.status !== 'processing'}
              className="scheduler-edit-select"
            >
              {statusOptions
                .filter((status: string) => editableStatuses.includes(status))
                .map((status: string) => (
                  <option key={status} value={status}>
                    {statusLabels[status]}
                  </option>
                ))}
            </select>
          ) : (
            <span className={`scheduler-status-chip ${entry.status}`}>{statusLabels[entry.status]}</span>
          )}
        </td>
        <td className="scheduler-table-cell scheduler-col-user">{entry.user_name}</td>
        <td className="scheduler-table-cell scheduler-col-edit-date">{formatDate(entry.edit_date)}</td>
        <td className="scheduler-table-cell scheduler-col-actions">
          {(entry.status === 'processing' || entry.status === 'failed') && !entryDue &&
            (isEditMode ? (
              <div className="scheduler-action-buttons">
                <button
                  className={`scheduler-action-button scheduler-save-action-button ${!hasItemChanges('entry', entry.id) ? 'disabled' : ''}`}
                  onClick={() => onSaveEdit('entry', entry.id)}
                  disabled={!hasItemChanges('entry', entry.id)}
                >
                  <Save sx={{ fontSize: '16px' }} />
                </button>
                <button
                  className="scheduler-action-button scheduler-cancel-action-button"
                  onClick={() => onCancelEditing('entry', entry.id)}
                >
                  <Cancel sx={{ fontSize: '16px' }} />
                </button>
              </div>
            ) : (
              <button
                className="scheduler-action-button scheduler-edit-button"
                onClick={() => onStartEditing('entry', entry.id, { status: entry.status })}
              >
                <Edit sx={{ fontSize: '16px' }} />
              </button>
            ))}
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={8} style={{ padding: 0 }}>
            <table className="scheduler-substage-table">
              <tbody>
                {entry.substages.map((substage: any) => (
                  <SchedulerSubstageRow
                    key={substage.id}
                    substage={substage}
                    onStartEditing={onStartEditing}
                    onCancelEditing={onCancelEditing}
                    onSaveEdit={onSaveEdit}
                    onUpdateEditingValue={onUpdateEditingValue}
                    isEditing={isEditing}
                    getEditingValue={getEditingValue}
                    hasItemChanges={hasItemChanges}
                  />
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}
